<template>
  <div class="theme-switch">
    <button
      v-for="option in options"
      :key="option.value"
      :class="[
        'theme-option',
        { 'theme-option--active': theme === option.value },
      ]"
      @click="theme = option.value"
    >
      <v-icon :icon="option.icon" size="24" />
      <span class="theme-option__label">{{ option.label }}</span>
    </button>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import { useTheme } from 'vuetify';
  import { useHead } from '@unhead/vue';
  import { useSettingStore, type ThemePreference } from '@/stores/settings';
  import { storeToRefs } from 'pinia';

  export default defineComponent({
    name: 'ThemeSwitch',
    setup() {
      const globalTheme = useTheme();
      const settingStore = useSettingStore();
      const { theme } = storeToRefs(settingStore);

      const options: { value: ThemePreference; label: string; icon: string }[] =
        [
          { value: 'system', label: 'Auto', icon: 'mdi-theme-light-dark' },
          { value: 'light', label: 'Light', icon: 'mdi-white-balance-sunny' },
          { value: 'dark', label: 'Dark', icon: 'mdi-weather-night' },
        ];

      useHead({
        meta: [
          {
            key: 'theme-color',
            name: 'theme-color',
            content: () => {
              return String(globalTheme.global.current.value.colors.primary);
            },
          },
        ],
      });

      return { theme, options };
    },
  });
</script>

<style scoped>
  .theme-switch {
    display: flex;
    gap: 8px;
  }

  .theme-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 80px;
    height: 72px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .theme-option:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }

  .theme-option--active {
    border-color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.08);
  }

  .theme-option__label {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  .theme-option--active .theme-option__label {
    color: rgb(var(--v-theme-primary));
  }
</style>
